import {
  keepPreviousData,
  queryOptions,
  useQuery,
  useSuspenseQuery,
  type UseQueryResult,
  type UseSuspenseQueryResult,
} from '@tanstack/react-query'

import { hasCode, isApiError } from '@/api/errors'
import { basketsKeys } from '@/features/baskets/api/keys'
import {
  BASKET_NOT_FOUND,
  BASKET_UNKNOWN_STOCK,
  basketsService,
  type BasketFilters,
} from '@/features/baskets/api/basketsService'
import type {
  BasketBacktest,
  BasketDetail,
  BasketInOut,
  BasketList,
  BasketStay,
  Verdict,
} from '@/schemas/baskets'

/** What `/baskets` opens on, and what Home's "Baskets to try" reads: the investors' list. */
export const DEFAULT_BASKET_FILTERS: BasketFilters = { audience: 'investor', tag: undefined }

/** One list: the `/baskets` page's main read, keyed on its tab and chip. */
export const basketsQueryOptions = (filters: BasketFilters) =>
  queryOptions<BasketList>({
    queryKey: basketsKeys.list(filters),
    queryFn: () => basketsService.list(filters),
  })

/**
 * Not suspense: a tab or chip change keeps the list on screen, marked as updating, until the new
 * one lands (docs/route-loading.md, "Search params"). Home reads the default list the same way.
 */
export const useBaskets = (
  filters: BasketFilters = DEFAULT_BASKET_FILTERS,
): UseQueryResult<BasketList> =>
  useQuery({ ...basketsQueryOptions(filters), placeholderData: keepPreviousData })

/**
 * One basket: the detail page's main read. Resolves to `null` on `BASKET_NOT_FOUND`
 * (docs/route-loading.md, "Not-found answers"), so the screen draws its not-found state instead of
 * the route's error page, whose "Try again" would get the same 404 forever.
 */
export const basketDetailQueryOptions = (slug: string) =>
  queryOptions<BasketDetail | null>({
    queryKey: basketsKeys.detail(slug),
    queryFn: async () => {
      try {
        return await basketsService.detail(slug)
      } catch (error) {
        if (hasCode(error, BASKET_NOT_FOUND)) return null
        throw error
      }
    },
  })

export const useSuspenseBasketDetail = (
  slug: string,
): UseSuspenseQueryResult<BasketDetail | null> => useSuspenseQuery(basketDetailQueryOptions(slug))

/** The History tab's stays. Secondary: the tab draws its own states. */
export const basketHistoryQueryOptions = (slug: string) =>
  queryOptions<BasketStay[]>({
    queryKey: basketsKeys.history(slug),
    queryFn: () => basketsService.history(slug),
  })

export const useBasketHistory = (slug: string): UseQueryResult<BasketStay[]> =>
  useQuery(basketHistoryQueryOptions(slug))

/**
 * Past results (mm-be #375). `null` on a 404 — an unknown basket, or the endpoint not deployed
 * yet — and the screen then draws no Past results tab, as it does for an empty series.
 */
export const basketBacktestQueryOptions = (slug: string) =>
  queryOptions<BasketBacktest | null>({
    queryKey: basketsKeys.backtest(slug),
    queryFn: async () => {
      try {
        return await basketsService.backtest(slug)
      } catch (error) {
        if (isApiError(error) && error.status === 404) return null
        throw error
      }
    },
  })

export const useBasketBacktest = (slug: string): UseQueryResult<BasketBacktest | null> =>
  useQuery(basketBacktestQueryOptions(slug))

/** The In or Out tab's list. Secondary: the tab draws its own states. */
export const basketInOutQueryOptions = (slug: string) =>
  queryOptions<BasketInOut>({
    queryKey: basketsKeys.inOut(slug),
    queryFn: () => basketsService.inOut(slug),
  })

export const useBasketInOut = (slug: string): UseQueryResult<BasketInOut> =>
  useQuery(basketInOutQueryOptions(slug))

/**
 * One company the In or Out list does not hold. `null` is `BASKET_UNKNOWN_STOCK`: the symbol is
 * not a listed stock, an answer to show, not a failure to retry. Never asked while `symbol` is
 * null, which is how the caller says the list already answered.
 */
export const useBasketCheck = (
  slug: string,
  symbol: string | null,
): UseQueryResult<Verdict | null> =>
  useQuery({
    queryKey: basketsKeys.check(slug, symbol ?? ''),
    queryFn: async () => {
      if (symbol === null) return null
      try {
        return await basketsService.check(slug, symbol)
      } catch (error) {
        if (hasCode(error, BASKET_UNKNOWN_STOCK)) return null
        throw error
      }
    },
    enabled: symbol !== null,
  })
